import { MoreVertOutlined } from '@mui/icons-material'
import { Divider, IconButton, Menu } from '@mui/material'
import { AppFeature } from 'features/AppModules/AppFeature'
import { FeatureKeys } from 'features/AppModules/FeatureKeys'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import { useRef, useState } from 'react'
import { useLocation } from 'react-router'
import { CheckManufacturabilityMenuItem } from '../BoMItemMenu/MenuItems/CheckManufacturabilityMenuItem'
import { CopyActivityKeywordsMenuItem } from '../BoMItemMenu/MenuItems/CopyActivityKeywordsMenuItem'
import { DeleteMenuItem } from '../BoMItemMenu/MenuItems/DeleteMenuItem'
import { DownloadBomItemFilesMenuItem } from '../BoMItemMenu/MenuItems/DownloadBomItemFilesMenuItem'
import { EventLogMenuItem } from '../BoMItemMenu/MenuItems/EventLogMenuItem'
import { OpenDetailsSidePanelButton } from '../BoMItemMenu/MenuItems/OpenDetailsSidePanelButton'
import { OpenOnErpMenuItem } from '../BoMItemMenu/MenuItems/OpenOnErpMenuItem'
import { PasteActivityKeywordsMenuItem } from '../BoMItemMenu/MenuItems/PasteActivityKeywordsMenuItem'
import { PurchasingPartMenuItem } from '../BoMItemMenu/MenuItems/PurchasingPartMenuItem'
import { RegressionTestMenuItem } from '../BoMItemMenu/MenuItems/RegressionTestMenuItem'
import { RememberOrForgetGeometryAsPurchasingMenuItem } from '../BoMItemMenu/MenuItems/RememberOrForgetPurchasingMenuItem'
import { ReportRecognitionIssuesMenuItem } from '../BoMItemMenu/MenuItems/ReportRecognitionIssuesMenuItem'
import { PartTypeFilterMenuButton } from './PartTypeFilterMenuButton'

type Props = {
  bomItemPointer: BomItemPointer
}

export const PartTypeMenuButton = ({ bomItemPointer }: Props) => {
  const [open, setOpen] = useState(false)
  const buttonRef = useRef(undefined)

  const search = useLocation().search

  let detailsIsOpen = false
  if (search.includes('bomItemId')) {
    detailsIsOpen = true
  }

  const handleClose = () => setTimeout(() => setOpen(false))

  return (
    <>
      <IconButton
        onClick={() => setOpen(true)}
        ref={buttonRef}
        size="small"
        color="inherit"
        data-testid={`bomitem-${bomItemPointer.id}-open-menu`}
        aria-label="open part type menu"
      >
        <MoreVertOutlined />
      </IconButton>
      <Menu
        open={open}
        onClose={() => setOpen(false)}
        anchorEl={buttonRef.current}
        sx={{
          '& .MuiPaper-root': {
            backgroundImage: 'none',
            overflow: 'visible',
          },
        }}
      >
        {!detailsIsOpen && (
          <AppFeature featureKey={FeatureKeys.bomItemDetails}>
            <OpenDetailsSidePanelButton
              bomItemPointer={bomItemPointer}
              onClose={handleClose}
              dataTestId={`bomitem-${bomItemPointer.id}-menu-open-details`}
            />
          </AppFeature>
        )}

        <AppFeature featureKey={FeatureKeys.quotation}>
          <PurchasingPartMenuItem
            bomItemPointer={bomItemPointer}
            onClose={handleClose}
            dataTestId={`bomitem-${bomItemPointer.id}-menu-set-purchasing`}
          />
        </AppFeature>

        {!detailsIsOpen && [
          <PasteActivityKeywordsMenuItem
            key="paste"
            bomItemPointer={bomItemPointer}
            onClose={handleClose}
            dataTestId={`bomitem-${bomItemPointer.id}-menu-paste-keywords`}
          />,
          <CopyActivityKeywordsMenuItem
            key="copy"
            bomItemPointer={bomItemPointer}
            onClose={handleClose}
            dataTestId={`bomitem-${bomItemPointer.id}-menu-copy-keywords`}
          />,
        ]}
        <Divider style={{ margin: 0 }} />
        <PartTypeFilterMenuButton
          bomItemPointer={bomItemPointer}
          onClose={handleClose}
        />
        <Divider style={{ margin: 0 }} />
        <RememberOrForgetGeometryAsPurchasingMenuItem
          bomItemPointer={bomItemPointer}
          onClose={handleClose}
          dataTestId={`bomitem-${bomItemPointer.id}-menu-remember-purchasing`}
        />
        <CheckManufacturabilityMenuItem
          bomItemPointer={bomItemPointer}
          onClose={handleClose}
          dataTestId={`bomitem-${bomItemPointer.id}-menu-check-manufacturability`}
        />
        <DownloadBomItemFilesMenuItem
          bomItemPointer={bomItemPointer}
          onClose={handleClose}
          dataTestId={`bomitem-${bomItemPointer.id}-menu-download-files`}
        />
        <OpenOnErpMenuItem
          bomItemPointer={bomItemPointer}
          onClose={handleClose}
          dataTestId={`bomitem-${bomItemPointer.id}-menu-open-in-erp`}
        />

        <Divider style={{ margin: 0 }} />
        <DeleteMenuItem
          bomItemPointer={bomItemPointer}
          onClose={handleClose}
          dataTestId={`bomitem-${bomItemPointer.id}-menu-delete`}
        />
        <Divider style={{ margin: 0 }} />
        <ReportRecognitionIssuesMenuItem
          bomItemPointer={bomItemPointer}
          onClose={handleClose}
          dataTestId={`bomitem-${bomItemPointer.id}-menu-report-issues`}
        />
        <RegressionTestMenuItem
          bomItemPointer={bomItemPointer}
          onClose={handleClose}
          dataTestId={`bomitem-${bomItemPointer.id}-menu-regression-test`}
        />
        <EventLogMenuItem
          bomItemPointer={bomItemPointer}
          onClose={handleClose}
          dataTestId={`bomitem-${bomItemPointer.id}-menu-view-event-logs`}
        />
      </Menu>
    </>
  )
}
